import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Mail, MapPin, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";

const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-[#1BA8E0] focus:ring-2 focus:ring-[#1BA8E0]/30";
const labelCls =
    "mb-2 block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400";

export default function Contact() {
    const [sent, setSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [name, setName] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setError("");
        const form = e.target;
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.elements["c-name"].value,
                    email: form.elements["c-email"].value,
                    phone: form.elements["c-phone"].value,
                    inquiry_type: form.elements["c-type"].value,
                    message: form.elements["c-msg"].value,
                }),
            });
            if (!res.ok) throw new Error("send failed");
            setSent(true);
        } catch {
            setError(
                "Something went wrong sending your inquiry. Please call (305) 697-9450 or email info@valueaddmanagement.com."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="grain relative overflow-hidden bg-[#0A1224] py-24 lg:py-32"
        >
            <div className="pointer-events-none absolute -left-40 bottom-0 h-[480px] w-[480px] rounded-full bg-[#1BA8E0]/10 blur-3xl" />
            <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:px-12">
                <Reveal>
                    <Eyebrow>Contact</Eyebrow>
                    <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        Contact Value Add Management
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                        For property management, operational partnerships, or multifamily investment
                        opportunities:
                    </p>
                    <div className="mt-10 space-y-6">
                        <a
                            data-testid="contact-phone"
                            href="tel:+13056979450"
                            className="group flex items-center gap-4"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#1BA8E0] transition-colors group-hover:border-[#1BA8E0]/50">
                                <Phone size={18} />
                            </span>
                            <span className="text-base font-semibold text-white transition-colors group-hover:text-[#1BA8E0]">
                                (305) 697-9450
                            </span>
                        </a>
                        <a
                            data-testid="contact-email"
                            href="mailto:info@valueaddmanagement.com"
                            className="group flex items-center gap-4"
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#1BA8E0] transition-colors group-hover:border-[#1BA8E0]/50">
                                <Mail size={18} />
                            </span>
                            <span className="text-base font-semibold text-white transition-colors group-hover:text-[#1BA8E0]">
                                info@valueaddmanagement.com
                            </span>
                        </a>
                        <div data-testid="contact-address" className="flex items-center gap-4">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-[#1BA8E0]">
                                <MapPin size={18} />
                            </span>
                            <span className="text-sm leading-relaxed text-slate-300">
                                1025 E Hallandale Beach Blvd, Suite 1600
                                <br />
                                Hallandale, Florida 33009
                            </span>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="rounded-[1.75rem] border border-white/10 bg-[#111D36]/60 p-7 backdrop-blur sm:p-9">
                        <AnimatePresence mode="wait">
                            {sent ? (
                                <motion.div
                                    key="ok"
                                    data-testid="contact-confirmation"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="flex min-h-[26rem] flex-col items-center justify-center text-center"
                                >
                                    <CheckCircle2 size={48} className="text-[#1BA8E0]" />
                                    <h3 className="mt-6 font-display text-2xl font-extrabold text-white">
                                        Inquiry received.
                                    </h3>
                                    <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-400">
                                        Thank you{name ? `, ${name.split(" ")[0]}` : ""}. Our team
                                        will follow up shortly. For immediate assistance, call (305)
                                        697-9450.
                                    </p>
                                    <button
                                        data-testid="contact-send-another"
                                        onClick={() => setSent(false)}
                                        className="mt-8 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-[#1BA8E0] hover:text-[#1BA8E0]"
                                    >
                                        Send another message
                                    </button>
                                </motion.div>
                            ) : (
                                <motion.form
                                    key="form"
                                    data-testid="contact-form"
                                    onSubmit={submit}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    className="space-y-5"
                                >
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="c-name">
                                                Name
                                            </label>
                                            <input
                                                id="c-name"
                                                required
                                                data-testid="contact-name-input"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Your name"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="c-email">
                                                Email
                                            </label>
                                            <input
                                                id="c-email"
                                                required
                                                type="email"
                                                data-testid="contact-email-input"
                                                placeholder="you@company.com"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                        <div>
                                            <label className={labelCls} htmlFor="c-phone">
                                                Phone
                                            </label>
                                            <input
                                                id="c-phone"
                                                data-testid="contact-phone-input"
                                                placeholder="(555) 000-0000"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelCls} htmlFor="c-type">
                                                I am a…
                                            </label>
                                            <select
                                                id="c-type"
                                                data-testid="contact-type-select"
                                                className={inputCls}
                                                defaultValue="Property Owner"
                                            >
                                                <option>Property Owner</option>
                                                <option>Investor</option>
                                                <option>Broker</option>
                                                <option>Lender</option>
                                                <option>Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelCls} htmlFor="c-msg">
                                            Message
                                        </label>
                                        <textarea
                                            id="c-msg"
                                            required
                                            rows={4}
                                            data-testid="contact-message-input"
                                            placeholder="Tell us about your property or opportunity"
                                            className={inputCls}
                                        />
                                    </div>
                                    {error && (
                                        <p
                                            data-testid="contact-error"
                                            className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300"
                                        >
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        data-testid="contact-submit-button"
                                        type="submit"
                                        disabled={sending}
                                        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1BA8E0] px-7 py-4 text-sm font-semibold text-[#06121F] transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {sending ? "Sending…" : "Submit Inquiry"}
                                        <ArrowUpRight
                                            size={17}
                                            className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                        />
                                    </button>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
